import React, { useEffect, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const Portfolio = () => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollPosition = 0;
    const scroll = () => {
      scrollPosition += 1;
      if (scrollPosition >= scrollContainer.scrollWidth / 2) {
        scrollPosition = 0;
      }
      scrollContainer.scrollLeft = scrollPosition;
    };

    const intervalId = setInterval(scroll, 30);
    return () => clearInterval(intervalId);
  }, []);

  const projects = [
    {
      name: 'DecaSol',
      description: 'Solana Contract Registry and Dev Tool - A comprehensive platform for managing and discovering Solana smart contracts with advanced search capabilities.',
      tags: ['Blockchain', 'Web3', 'Solana'],
      link: 'https://www.decasol.store/',
    },
    {
      name: 'Merged & Share',
      description: 'A platform to showcase your open source contributions as proof of work. Track, display, and share your GitHub contributions with the community.',
      tags: ['Open Source', 'GitHub', 'Portfolio'],
      link: 'https://merged-n-share.vercel.app/',
    },
    {
      name: 'PinBot',
      description: 'A smart tool that allows you to chat with your Discord channel pins. Never lose important messages again with AI-powered search and retrieval.',
      tags: ['Discord', 'AI', 'Chatbot'],
      link: 'https://pinbot-app.vercel.app/',
    },
    {
      name: 'Multi-LLM Chat',
      description: 'A Multi Model LLM aggregator that lets you compare responses from different AI models in real-time. Perfect for developers and AI enthusiasts.',
      tags: ['AI', 'LLM', 'Comparison'],
      link: 'https://t3chat.xyz/',
    },
    {
      name: 'AI Prep Platform',
      description: 'Interactive AI-powered learning platform for competitive exam preparation. Features adaptive learning, mock tests, and personalized study plans.',
      tags: ['Education', 'AI', 'SaaS'],
      link: '#',
    }
  ];

  // Duplicate projects for infinite scroll effect
  const duplicatedProjects = [...projects, ...projects];

  return (
    <section id="portfolio" className="py-24 px-6 relative bg-gradient-to-b from-black to-gray-900 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-white/60 font-semibold text-sm uppercase tracking-wide">Portfolio</span>
          <h2 className="text-4xl md:text-5xl font-extrabold mt-4 mb-4 text-white">
            Our Latest Work
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">Real products we've built for real clients</p>
        </div>

        {/* Animated Carousel */}
        <div className="relative">
          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-hidden scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {duplicatedProjects.map((project, index) => (
              <Card
                key={index}
                className="bg-white/5 border-white/10 hover:border-white/30 transition-all duration-300 hover:transform hover:-translate-y-2 group cursor-pointer backdrop-blur-sm flex-shrink-0 w-[400px]"
                onClick={() => project.link !== '#' && window.open(project.link, '_blank')}
              >
                <CardContent className="p-0">
                  <div className="relative h-48 bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                    <div className="relative z-10 text-6xl font-extrabold text-white/20 group-hover:text-white/30 transition-colors duration-300">
                      {project.name.charAt(0)}
                    </div>
                    {project.link !== '#' && (
                      <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-sm rounded-full p-2 group-hover:bg-white/20 transition-colors duration-300">
                        <ExternalLink className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 text-white group-hover:text-gray-200 transition-colors duration-300">
                      {project.name}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 leading-relaxed line-clamp-3">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, idx) => (
                        <Badge key={idx} variant="outline" className="border-white/20 text-white/80 hover:border-white/40 hover:bg-white/5">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Gradient Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-900 to-transparent pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-900 to-transparent pointer-events-none"></div>
        </div>

        {/* Manual Grid for Mobile */}
        <div className="grid md:hidden grid-cols-1 gap-6 mt-8">
          {projects.map((project, index) => (
            <Card
              key={index}
              className="bg-white/5 border-white/10 hover:border-white/30 transition-all duration-300 group cursor-pointer backdrop-blur-sm"
              onClick={() => project.link !== '#' && window.open(project.link, '_blank')}
            >
              <CardContent className="p-0">
                <div className="relative h-48 bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                  <div className="relative z-10 text-6xl font-extrabold text-white/20 group-hover:text-white/30 transition-colors duration-300">
                    {project.name.charAt(0)}
                  </div>
                  {project.link !== '#' && (
                    <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-sm rounded-full p-2 group-hover:bg-white/20 transition-colors duration-300">
                      <ExternalLink className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 text-white group-hover:text-gray-200 transition-colors duration-300">
                    {project.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="border-white/20 text-white/80 hover:border-white/40 hover:bg-white/5">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;